import { Directive, ElementRef, inject, input, OnDestroy, OnInit, DOCUMENT } from '@angular/core';

/**
 * Keeps a CSS custom property in sync with the height of a reference element
 * (by default the sticky app header). This lets sticky content inside the host
 * element park itself just below that reference element instead of behind it.
 *
 * Usage:
 *   <div appStickyOffset>
 *     ... table with `top: var(--app-header-height)` sticky header ...
 *   </div>
 *
 * The reference selector and the CSS variable name are configurable:
 *   <div appStickyOffset referenceSelector="app-header header"
 *        cssVariable="--app-header-height"></div>
 */
@Directive({
  selector: '[appStickyOffset]'
})
export class StickyOffsetDirective implements OnInit, OnDestroy {
  /** CSS selector for the element whose height drives the offset. */
  readonly referenceSelector = input<string>('app-header header');

  /** Name of the CSS custom property set on the host element. */
  readonly cssVariable = input<string>('--app-header-height');

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly document = inject(DOCUMENT);
  private resizeObserver?: ResizeObserver;

  ngOnInit() {
    const reference = this.document.querySelector(this.referenceSelector()) as HTMLElement | null;
    if (!reference) {
      return;
    }

    const applyHeight = () => {
      const height = reference.getBoundingClientRect().height;
      this.host.nativeElement.style.setProperty(this.cssVariable(), `${height}px`);
    };

    applyHeight();

    this.resizeObserver = new ResizeObserver(applyHeight);
    this.resizeObserver.observe(reference);
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
  }
}
